import { newSpinner } from '@dsee/components/popups/spinner.js';
import { showError } from '@dsee/components/popups/message.js';
import date from '@dsee/util/date.js';
import { deepTransform } from '@dsee/util/objects.js';


function openSpinner(blocking) {
    if (!blocking)
        return () => {
        };

    const spinner = newSpinner();
    spinner.open();
    return spinner.close;
}


function dateTransformer(data) {
    return deepTransform(data, (value) => {
        if (value instanceof Date)
            return date(value, 'yyyy-MM-ddTHH:mm:ss.SSSZC');
        return value;
    });
}


function fixRequestUrl(request, url) {
    request.url = url;

    if (!request.params)
        return;

    request.url += url.includes('?') ? '&' : '?';
    request.url += new URLSearchParams(request.params);
}


function dontOverwriteBody(body) {
    return typeof (body) === 'string'
        || body instanceof Blob
        || body instanceof File
        || body instanceof FormData
        || body instanceof URLSearchParams
        || body instanceof ArrayBuffer
        || ArrayBuffer.isView(body);
}


const NO_BODY_METHODS = new Set([ 'HEAD', 'GET' ]);

function fixRequestBody(request, body) {
    request.method = request.method.toUpperCase();

    if (NO_BODY_METHODS.has(request.method)) {
        request.body = null;
    } else if (dontOverwriteBody(body)) {
        request.body = body;
    } else {
        request.body = JSON.stringify(dateTransformer(body));
        request.headers['Content-Type'] = 'application/json;charset=utf-8';
    }

}


const INVALID_SERVER_RESPONSE = 'Нам жаль, но что-то пошло не так.\nПопробуйте повторить попытку позднее.';

async function makeRequest(request) {
    try {
        const response = await fetch(request.url, request);

        if (request.responseType === 'blob')
            return await response.blob();

        return await response.json();
    } catch (e) {
        const error = new Error(INVALID_SERVER_RESPONSE);
        error.stack += '\nCaused by: ' + e.stack;
        throw error;
    }
}

function checkResponse(data) {
    if (typeof (data) !== 'object') {
        console.error(data);
        throw new Error(INVALID_SERVER_RESPONSE);
    }

    if (data.errorCode === 'error.NotAuthorizedException') {
        if (window.location.pathname === '/user/login')
            return data;

        const params = {
            redirect: window.location.pathname,
        };
        window.location.href = '/user/login?' + new URLSearchParams(params);

    } else if (data.errorCode === 'error.UnderMaintenanceException') {
        if (window.location.pathname === '/tech-works' || window.location.pathname === '/user/login') {
            const error = new Error(data.errorText);
            error.code = data.errorCode;
            throw error;
        }

        const params = {
            redirect: window.location.pathname,
        };
        window.location.href = '/tech-works?' + new URLSearchParams(params);
    } else if (data.errorCode === 'error.Reject') {
        const error = new Error(data.errorText + '\n Попробуйте позже или обратитесь в <a href="https://helpme.rt.ru/tickets/create/sm/3806" target="_blank">Helpme</a>');
        error.code = data.errorCode;
        throw error;
    } else if (data.errorCode) {
        const error = new Error(data.errorText);
        error.code = data.errorCode;
        throw error;
    }

    return data;
}


/**
 * Объект ответа
 *
 * @typedef {Object} Response
 * @template {T} Тип ответа
 * @property {T} result Содержимое
 */
/**
 * Опции запроса
 *
 * @typedef {Object} RequestOptions
 * @property {string}  method  Метод запроса (по умолчанию, post)
 * @property {Object}  params  Query-параметры запроса (по умолчанию, {})
 * @property {Object}  headers Заголовки запроса (по умолчанию, {})
 * @property {boolean} blocking Флаг отображения окна загрузки (по умолчанию, true)
 * @property {boolean} messages Флаг отображения сообщений об ошибке (по умолчанию, true)
 */
/**
 * Общий запрос к бекенду
 *
 * @param {string} url относительный url запроса
 * @param {Object} body объект запроса
 * @param {RequestOptions<Object>} opts опции запроса
 * @returns {Response<Object>} объект ответа
 */
export async function request(url, body = {}, opts = {}) {
    if (typeof (url) !== 'string')
        throw new TypeError('Parameter #1:url is not a string');

    if (body != null && typeof (body) !== 'object')
        throw new TypeError('Parameter #2:body is not an object');

    if (opts == null || typeof (opts) !== 'object')
        throw new TypeError('Parameter #3:opts is not an object');

    const request = {
        method: 'POST',
        blocking: false,
        messages: true,
        headers: {},
        responseType: 'json',
        ...opts,
    };

    fixRequestUrl(request, url);
    fixRequestBody(request, body);

    const closeSpinner = openSpinner(request.blocking);
    let error;

    try {
        const data = await makeRequest(request);
        return checkResponse(data);
    } catch (e) {
        console.warn('request to ' + request.url + ' failed: ', e);
        error = e;
    } finally {
        closeSpinner();
    }

    const showMessages = request.messages;
    if (showMessages) {
        if (typeof (showMessages) === 'function') {
            await showMessages(error.message);
        } else {
            showError(error.message);
        }
    }

    throw error;
}


export function boundApiRequest(prefix) {
    return (url, ...rest) => request(prefix + url, ...rest);
}
